import type { MouseEvent, ReactNode } from "react";
import { useCallback } from "react";

import { colorsPalettes, elevation } from "@similarweb/styles";
import { Modal } from "@similarweb/ui-components";

import { Next, Prev } from "./NavButton";

const customModalStyles = {
    content: {
        width: "880px",
        maxHeight: "90vh",
        backgroundColor: colorsPalettes.carbon[0],
        borderRadius: "6px",
        boxShadow: elevation.Z4,
    },
};

interface IModalShellProps {
    selectedIndex: number;
    itemCount: number;
    onIndexChange: (index: number) => void;
    onClose: () => void;
    header: ReactNode;
    children: ReactNode;
    dataAutomation?: string;
}

export const ModalShell = ({
    selectedIndex,
    itemCount,
    onIndexChange,
    onClose,
    header,
    children,
    dataAutomation,
}: IModalShellProps) => {
    const maxIndex = itemCount - 1;

    const handlePrevious = useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            onIndexChange(Math.max(0, selectedIndex - 1));
        },
        [onIndexChange, selectedIndex],
    );

    const handleNext = useCallback(
        (event: MouseEvent<HTMLButtonElement>) => {
            event.stopPropagation();
            onIndexChange(Math.min(maxIndex, selectedIndex + 1));
        },
        [onIndexChange, selectedIndex, maxIndex],
    );

    return (
        <Modal
            isOpen
            onRequestClose={onClose}
            customStyles={customModalStyles}
            data-automation={dataAutomation}
        >
            {header}

            <Modal.Content>{children}</Modal.Content>

            <Prev
                iconName="arrow-left"
                type="flat"
                onClick={handlePrevious}
                isDisabled={selectedIndex === 0}
                aria-label="Previous item"
                data-automation="nav-button-prev"
            />

            <Next
                iconName="arrow-right"
                type="flat"
                onClick={handleNext}
                isDisabled={selectedIndex === maxIndex}
                aria-label="Next item"
                data-automation="nav-button-next"
            />
        </Modal>
    );
};
